import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useApproveTicket } from 'hooks/api/useTickets';

type UseTicketApprovePopupT = {
  isApprovingTicket: boolean;
  isTicketSuccessfullyApproved: boolean;
  isTicketApprovingError: boolean;
  approveTicketErrorMessage: string | undefined;
  approveTicketValidationErrorMessage: string | undefined;
  showPopupContent: boolean;
  handleApproveTicket: (ticketIds: string[]) => NoopT;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useTicketApprovePopup = (): UseTicketApprovePopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();

  /**
   * Api hooks
   */
  const approveTicketMutation = useApproveTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
    },
  });

  /**
   * Variables
   */
  const approveTicketErrorMessage = useMemo(
    () => getErrorMessage(approveTicketMutation.error),
    [approveTicketMutation.error]
  );
  const approveTicketValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(approveTicketMutation.error),
    [approveTicketMutation.error]
  );

  /**
   * Handlers
   */
  const resetApproveTicketMutationState = useCallback(() => {
    if (approveTicketMutation.isSuccess || approveTicketMutation.isError) {
      approveTicketMutation.reset();
    }
  }, [approveTicketMutation]);
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
      resetApproveTicketMutationState();
    },
    [resetApproveTicketMutationState]
  );
  const handleApproveTicket = useCallback(
    (ticketIds: string[]) => () => {
      approveTicketMutation.mutate({ ticketIds });
    },
    [approveTicketMutation]
  );

  return {
    isApprovingTicket: approveTicketMutation.isLoading,
    isTicketSuccessfullyApproved: approveTicketMutation.isSuccess,
    isTicketApprovingError: approveTicketMutation.isError,
    approveTicketErrorMessage,
    approveTicketValidationErrorMessage,
    showPopupContent: approveTicketMutation.isIdle || approveTicketMutation.isLoading,
    handleApproveTicket,
    handleClosePopup,
  };
};

export default useTicketApprovePopup;
